// Host-rendered, unspoofable consent. The broker calls `consentPrompt.request`,
// which parks a pending request in this store; the ConsentModal (mounted by the
// host, never by a plugin) renders it and resolves the user's choice.

import { create } from 'zustand';
import type { PermissionCapability } from '@slate/plugin-api';
import type { ConsentPrompt } from './permissionBroker';

export interface PendingConsent {
  pluginId: string;
  capability: PermissionCapability;
  reason: string;
  resolve: (granted: boolean) => void;
}

interface ConsentState {
  pending: PendingConsent | null;
  request(pluginId: string, capability: PermissionCapability, reason: string): Promise<boolean>;
  respond(granted: boolean): void;
}

export const useConsentStore = create<ConsentState>((set, get) => ({
  pending: null,
  request: (pluginId, capability, reason) =>
    new Promise<boolean>((resolve) => {
      set({ pending: { pluginId, capability, reason, resolve } });
    }),
  respond: (granted) => {
    const p = get().pending;
    if (p) {
      p.resolve(granted);
      set({ pending: null });
    }
  },
}));

/** The ConsentPrompt the broker uses, backed by the store above. */
export const consentPrompt: ConsentPrompt = {
  request: (pluginId, capability, reason) =>
    useConsentStore.getState().request(pluginId, capability, reason),
};
